import { type PropsWithChildren } from 'react';

import { useGridPlotSize } from '../../core/components/chart-grid/hooks/useGridPlotSize.js';
import { defaultUnitFormatter } from '../../core/utils/defaultUnitFormatter.js';
import {
  getFormattedTicks,
  getFormatterByTicks,
} from '../../core/utils/formatter-by-ticks.js';
import {
  getHorizontalNumTicks,
  getVerticalNumTicks,
} from '../../core/utils/ticks.js';
import { CategoricalBarChartValueFormatter } from '../contexts/CategoricalBarChartValueFormatter.context.js';
import { CategoricalBarChartTicks } from '../contexts/CategoricalBarChartValueTicks.context.js';
import { useCategoricalBarChartBaseConfig } from '../hooks/useCategoricalBarChartBaseConfig.js';
import { useCategoricalBarChartScales } from '../hooks/useCategoricalBarChartScales.js';
import { useCategoricalBarChartValueAxisConfig } from '../hooks/useCategoricalBarChartValueAxisConfig.js';
import { useCategoricalBarFilteredData } from '../hooks/useCategoricalBarFilteredData.js';
import { isVertical } from '../utils/layout.js';

export const CategoricalBarChartFormatterProvider = ({
  children,
}: PropsWithChildren) => {
  const { height, width } = useGridPlotSize();
  const { valueRepresentation } = useCategoricalBarChartBaseConfig();

  const { valueScale } = useCategoricalBarChartScales();
  const valueAxisConfig = useCategoricalBarChartValueAxisConfig();
  const { unit } = useCategoricalBarFilteredData();
  const { layout } = useCategoricalBarChartBaseConfig();
  const isHorizontal = !isVertical(layout);
  const isRelative = valueRepresentation === 'relative';

  const verticalNumTicks = getVerticalNumTicks(height);
  const horizontalNumTicks = getHorizontalNumTicks(width);
  const numTicks = isHorizontal ? horizontalNumTicks : verticalNumTicks;

  //TODO: test relative logic
  const valueFormatter = isRelative
    ? defaultUnitFormatter(unit)
    : getFormatterByTicks(
        numTicks,
        valueScale,
        unit,
        valueAxisConfig.formatter,
      );

  const valueAxisTicks = getFormattedTicks(
    numTicks,
    valueScale,
    unit,
    valueFormatter,
  );

  return (
    <CategoricalBarChartValueFormatter.Provider value={valueFormatter}>
      <CategoricalBarChartTicks.Provider value={{ valueAxis: valueAxisTicks }}>
        {children}
      </CategoricalBarChartTicks.Provider>
    </CategoricalBarChartValueFormatter.Provider>
  );
};

CategoricalBarChartFormatterProvider['displayName'] =
  'CategoricalBarChartFormatterProvider';
